import get from 'lodash/get';
import { SEARCH_TYPES } from '../constants/index';
import {
    configOptions, shortAlgorithmConfig, configKeys, gapCostOptionsByAlgorithm,
} from './constantConfig';

// 配置项类型
class SettingItem {
    constructor({
        key, label, value, defaultValue, options, initValue,
    }) {
        this.key = key;
        this.label = label;
        this.value = value;
        this.defaultValue = defaultValue;
        this.options = options;
        this.initValue = initValue;
        this.active = true;
    }
}

function setLowComplexRegion(seqTypeAndDbType) {
    const commonAlgorithmConfig = configOptions[seqTypeAndDbType];
    if (this.lowComplexRegion && commonAlgorithmConfig) {
        this.lowComplexRegion.value = commonAlgorithmConfig.lowComplexityRegion;
    }
}
// 暂存最后一次的默认算法的值，当目标序列类型或查询序列类型变更时赋值回去
export const storeLastDefaultAlgorithm = {
    isStore: false,
    value: undefined,
};

function setGapCostOptions(seqTypeAndDbType, algorithm, useDefaultValue = true) {
    let gapOptions;
    if (seqTypeAndDbType === configKeys.nucTypeNucDbConfigs) {
        gapOptions = gapCostOptionsByAlgorithm[algorithm][this.matchScores.value];
    } else {
        gapOptions = gapCostOptionsByAlgorithm[algorithm][this.matrix.value];
    }

    this.gaps.options = gapOptions;
    this.gaps.defaultValue = get(gapOptions.find((x) => x.isDefault), 'value');
    // 用户设置的值不合理，用默认值覆盖
    if (!gapOptions.find((x) => x.value === this.gaps.value)) {
        this.gaps.value = this.gaps.defaultValue;
    }

    if (useDefaultValue) {
        this.gaps.value = this.gaps.defaultValue;
    }
}

function setAlgorithm(seqTypeAndDbType, useDefaultValue) {
    // 勾选默认算法，其他配置项都是默认值,并且置为禁用状态
    // 设置可选项的值
    // 蛋白质查蛋白 | 蛋白质查核酸 | 核酸查蛋白质 | 核酸查核酸
    const { algorithmOptions } = configOptions[seqTypeAndDbType];

    if (seqTypeAndDbType === configKeys.nucTypeProtDbConfigs || seqTypeAndDbType === configKeys.protTypeNucDbConfigs) {
        this.defaultAlgorithm.active = false;
        storeLastDefaultAlgorithm.value = this.defaultAlgorithm.value;
        storeLastDefaultAlgorithm.isStore = true;
        this.defaultAlgorithm.value = false;
    } else {
        this.defaultAlgorithm.active = true;
    }

    if (this.algorithm && !Object.isFrozen(this.algorithm)) {
        this.algorithm.options = algorithmOptions;
        this.algorithm.defaultValue = algorithmOptions.find((x) => x.default).value;
        if (!this.algorithm.options.find((x) => x.value === this.algorithm.value)) {
            // 当前的值不在下拉列表里，把默认值赋值给当前选中的算法
            this.algorithm.value = this.algorithm.defaultValue;
        }
    }
    // 获取当前选中的算法 对应的配置项
    const algorithmFields = algorithmOptions.find((x) => x.value === this.algorithm.value).children;

    if (this.matrix) {
        if (algorithmFields.matrix) {
            this.matrix.options = algorithmFields.matrix;
            this.matrix.defaultValue = get(algorithmFields.matrix.find((x) => x.default), 'value');
            if (useDefaultValue) {
                this.matrix.value = this.matrix.defaultValue;
            }
        }
        this.matrix.active = !!algorithmFields.matrix;
    }

    if (this.wordSize) {
        if (algorithmFields.wordSize) {
            this.wordSize.options = algorithmFields.wordSize;
            this.wordSize.defaultValue = get(algorithmFields.wordSize.find((x) => x.default), 'value');
            if (useDefaultValue) {
                this.wordSize.value = this.wordSize.defaultValue;
            }
        }
        this.wordSize.active = !!algorithmFields.wordSize;
    }

    if (this.matchScores) {
        if (algorithmFields.matchScores) {
            this.matchScores.options = algorithmFields.matchScores;
            this.matchScores.defaultValue = get(algorithmFields.matchScores.find((x) => x.default), 'value');
            if (useDefaultValue) {
                this.matchScores.value = this.matchScores.defaultValue;
            }
        }
        this.matchScores.active = !!algorithmFields.matchScores;
    }

    if (this.gaps) {
        this.setGapCostOptions(seqTypeAndDbType, this.algorithm.value, useDefaultValue);
        this.gaps.active = !!this.gaps.options.length;
    }
}

export function createSettingByType(type, mismatchMode) {
    const sequenceType = new SettingItem({
        key: 'sequenceType',
        label: 'Search.labels.predicted',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const dbType = new SettingItem({
        key: 'dbType',
        label: 'Search.labels.database',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const sequenceLength = new SettingItem({
        key: 'subject_length',
        label: 'Search.labels.subjectLength',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const multiSequenceLength = new SettingItem({
        key: 'multi_subject_length',
        label: 'Search.labels.subjectLength',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const similarity = new SettingItem({
        key: 'perc_identity',
        label: 'Search.labels.similarity',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const querySimilarity = new SettingItem({
        key: 'q_perc_identity',
        label: 'Search.labels.queryIdentity',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const queryCoverage = new SettingItem({
        key: 'qcov_hsp_perc',
        label: 'Search.labels.qconv',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const subjectSimilarity = new SettingItem({
        key: 's_perc_identity',
        label: 'Search.labels.subjectIdentity',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const subjectCoverage = new SettingItem({
        key: 'scov_hsp_perc',
        label: 'Search.labels.subjectCoverage',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const targetSeqLimit = new SettingItem({
        key: 'limit',
        label: 'Search.labels.subjectCoverage',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const ungapped = new SettingItem({
        key: 'ungapped',
        label: 'Search.labels.gaps',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const eValue = new SettingItem({
        key: 'evalue',
        label: 'Search.labels.evalue',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const lowComplexRegion = new SettingItem({
        key: 'lowComplexityRegion',
        label: 'Search.labels.lowComplexRegion',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const defaultAlgorithm = new SettingItem({
        key: 'automatic',
        label: 'Search.labels.defaultAlgorithm',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const algorithm = new SettingItem({
        key: 'algorithm',
        label: 'Search.labels.algorithm',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const matrix = new SettingItem({
        key: 'matrix',
        label: 'Search.labels.matrix',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const wordSize = new SettingItem({
        key: 'wordSize',
        label: 'Search.labels.wordSize',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const gaps = new SettingItem({
        key: 'gaps',
        label: 'Search.labels.gapCosts',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    const matchScores = new SettingItem({
        key: 'matchScores',
        label: 'Search.labels.matchScores',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });
    // eslint-disable-next-line
    const mismatches = new SettingItem({
        key: 'mismatches',
        label: 'Search.labels.mismatches',
        value: '',
        defaultValue: '',
        options: [],
        initValue: '',
    });

    let settingObj;

    switch (type) {
    case SEARCH_TYPES.sequence:
    case SEARCH_TYPES.degenerate:
    case SEARCH_TYPES.combined:
        settingObj = {
            sequenceType,
            dbType,
            sequenceLength,
            multiSequenceLength,
            similarity,
            querySimilarity,
            queryCoverage,
            subjectSimilarity,
            subjectCoverage,
            targetSeqLimit,
            ungapped,
            eValue,
            lowComplexRegion,
            defaultAlgorithm,
            algorithm,
            matrix,
            wordSize,
            gaps,
            matchScores,
            // 设置序列长度的值
            setSequenceLengthType(isMulti) {
                this.sequenceLength.active = !isMulti;
                this.multiSequenceLength.active = isMulti;
            },
            // 设置算法的值
            setAlgorithm(seqTypeAndDbType, useDefaultValue) {
                setAlgorithm.bind(this)(seqTypeAndDbType, useDefaultValue);
            },
            setGapCostOptions(seqTypeAndDbType, useDefaultValue) {
                setGapCostOptions.bind(this)(seqTypeAndDbType, this.algorithm.value, useDefaultValue);
            },
            setLowComplexRegion(seqTypeAndDbType) {
                setLowComplexRegion.bind(this)(seqTypeAndDbType);
            },
        };
        break;
    case SEARCH_TYPES.short:
        algorithm.active = false;
        algorithm.value = 'blastn-short';
        Object.freeze(algorithm);
        settingObj = {
            name: 'short',
            sequenceType,
            dbType,
            sequenceLength,
            similarity,
            querySimilarity,
            queryCoverage,
            subjectSimilarity,
            subjectCoverage,
            targetSeqLimit,
            ungapped,
            eValue,
            algorithm,
            lowComplexRegion,
            wordSize,
            // 设置算法的值
            setAlgorithm(seqTypeAndDbType, useDefaultValue) {
                const algorithmFields = shortAlgorithmConfig[seqTypeAndDbType];
                if (this.wordSize) {
                    if (algorithmFields.wordSize) {
                        this.wordSize.options = algorithmFields.wordSize;
                        this.wordSize.defaultValue = get(algorithmFields.wordSize.find((x) => x.default), 'value');
                        if (useDefaultValue
                            || !this.wordSize.options.find((x) => x.value === String(this.wordSize.value))) {
                            this.wordSize.value = this.wordSize.defaultValue;
                        }
                    }
                    this.wordSize.active = !!algorithmFields.wordSize;
                }
            },
            setLowComplexRegion(seqTypeAndDbType) {
                setLowComplexRegion.bind(this)(seqTypeAndDbType);
            },
        };
        break;
    case SEARCH_TYPES.motif:
        settingObj = {
            sequenceType,
            dbType,
            sequenceLength,
            similarity,
            querySimilarity,
            subjectSimilarity,
            eValue,
            lowComplexRegion,
        };
        break;
    case SEARCH_TYPES.motif2:
        settingObj = {
            sequenceType,
            dbType,
            sequenceLength,
            subjectCoverage,
        };
        break;
    case SEARCH_TYPES.fragment:
        settingObj = {
            sequenceType,
            dbType,
            similarity,
            eValue,
            lowComplexRegion,
            sequenceLength,
            setLowComplexRegion(seqTypeAndDbType) {
                setLowComplexRegion.bind(this)(seqTypeAndDbType);
            },
        };
        break;
    case SEARCH_TYPES.antibody:
        sequenceType.active = false;
        dbType.active = false;
        settingObj = {
            sequenceType,
            dbType,
            sequenceLength,
            similarity,
            querySimilarity,
            queryCoverage,
            subjectSimilarity,
            subjectCoverage,
            ungapped,
            eValue,
            lowComplexRegion,
        };
        break;
    default:
        settingObj = {};
        break;
    }

    if (mismatchMode) {
        sequenceType.active = false;
        dbType.active = false;
        settingObj = {
            sequenceType,
            dbType,
            mismatches,
        };
    }

    return settingObj;
}
